import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Copy, Share2, Facebook, Twitter, Instagram, Music2, BookOpen, Radio, CalendarDays, PenLine, Link2, Image as ImageIcon, Download, Clock, Trash2, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls = "mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPES = [
  { key: "devotion", label: "Daily Devotion", icon: BookOpen },
  { key: "promo", label: "Listen Live", icon: Radio },
  { key: "event", label: "Event", icon: CalendarDays },
  { key: "custom", label: "Custom", icon: PenLine },
];

export default function SocialMedia() {
  const [ctx, setCtx] = useState(null);
  const [type, setType] = useState("devotion");
  const [context, setContext] = useState("");
  const [caption, setCaption] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [selEvent, setSelEvent] = useState(0);
  const [img, setImg] = useState("");
  const [imgBusy, setImgBusy] = useState(false);
  const [theme, setTheme] = useState("navy");
  const [schedule, setSchedule] = useState([]);
  const [bestTimes, setBestTimes] = useState([]);
  const [remindAt, setRemindAt] = useState("09:00");
  const [queuing, setQueuing] = useState(false);
  const [autoQueue, setAutoQueue] = useState(true);

  useEffect(() => { api.get("/social/context").then((r) => setCtx(r.data)).catch(() => {}); }, []);
  const loadSchedule = () => api.get("/social/schedule").then((r) => { setSchedule(r.data.posts || []); setBestTimes(r.data.best_times || []); }).catch(() => {});
  useEffect(() => { loadSchedule(); api.get("/social/settings").then((r) => setAutoQueue(r.data.auto_queue_devotion)).catch(() => {}); }, []);

  // Prefill context text + link when type or source changes.
  useEffect(() => {
    if (!ctx) return;
    if (type === "devotion" && ctx.devotion) {
      setContext(`Title: ${ctx.devotion.title}\nVerse: "${ctx.devotion.verse_text}" — ${ctx.devotion.verse_reference}\n${ctx.devotion.reflection}`);
      setLink(ctx.devotion.link);
    } else if (type === "promo") {
      setContext(`In His Image Radio is streaming gospel music 24/7. Stations include ${(ctx.stations || []).slice(0, 4).join(", ")}. Tune in now!`);
      setLink(ctx.listen_url);
    } else if (type === "event") {
      const e = (ctx.events || [])[selEvent];
      if (e) { setContext(`Event: ${e.title}\nWhen: ${e.starts_at}\nWhere: ${e.venue}${e.city ? ", " + e.city : ""}`); setLink(e.link); }
      else { setContext(""); setLink(ctx.site_url); }
    } else {
      setLink(ctx.site_url || "");
    }
  }, [type, ctx, selEvent]);

  const generate = async (platform = "general") => {
    setBusy(true);
    try {
      const r = await api.post("/social/generate", { type, platform, context });
      setCaption(r.data.caption);
      toast.success("Caption generated — edit as you like");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate caption"); }
    finally { setBusy(false); }
  };

  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); }
    catch { toast.error("Could not copy"); }
  };
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(caption)}`, "_blank", "noopener,width=650,height=650");
  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(link)}`, "_blank", "noopener,width=650,height=650");
  const copyForInstagram = async () => { await copy(`${caption}${link ? "\n" + link : ""}`, "Caption"); toast.message("Paste it into Instagram (link in bio)"); };
  const copyForTikTok = async () => { await copy(`${caption}${link ? "\n" + link : ""}`, "Caption"); toast.message("Paste it into TikTok when posting your video"); };

  const imagePayload = () => {
    if (type === "devotion" && ctx?.devotion) return { title: ctx.devotion.title, verse_text: ctx.devotion.verse_text, verse_reference: ctx.devotion.verse_reference, footer: "Read today's devotion" };
    if (type === "promo") return { title: "Streaming Gospel 24/7", verse_text: "Tune in and let His praises fill your day.", verse_reference: "", footer: "Listen live \u00b7 In His Image Radio" };
    if (type === "event") { const e = (ctx?.events || [])[selEvent]; return { title: e?.title || "Upcoming Event", verse_text: [e?.venue, e?.city].filter(Boolean).join(", "), verse_reference: "", footer: "In His Image Radio" }; }
    return { title: "", verse_text: context.slice(0, 160), verse_reference: "", footer: "In His Image Radio" };
  };
  const genImage = async () => {
    setImgBusy(true);
    try {
      const r = await api.post("/social/image", { ...imagePayload(), theme });
      setImg(r.data.image);
      toast.success("Post image ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build image"); }
    finally { setImgBusy(false); }
  };
  const downloadImage = () => {
    const a = document.createElement("a");
    a.href = img; a.download = `iihr-${type}-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const changePhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/devotions/author-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      await genImage();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update photo"); setImgBusy(false); }
  };

  const queuePost = async () => {
    if (!caption.trim()) { toast.error("Generate or write a caption first"); return; }
    setQueuing(true);
    try {
      await api.post("/social/schedule", { type, caption, link, remind_at: remindAt });
      toast.success("Queued — we'll remind you to post it");
      loadSchedule();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not queue post"); }
    finally { setQueuing(false); }
  };
  const removeQueued = async (id) => {
    try { await api.delete(`/social/schedule/${id}`); loadSchedule(); }
    catch { toast.error("Could not remove"); }
  };
  const markPosted = async (id) => {
    try { await api.post(`/social/schedule/${id}/posted`); toast.success("Nice — marked as posted"); loadSchedule(); }
    catch { toast.error("Could not update"); }
  };
  const fmtTime = (t) => { try { const [h, m] = t.split(":"); const d = new Date(); d.setHours(+h, +m); return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return t; } };
  const typeLabel = (k) => (TYPES.find((t) => t.key === k)?.label || "Custom");
  const grabAndPost = async (p) => {
    try { await navigator.clipboard.writeText(`${p.caption}${p.link ? "\n" + p.link : ""}`); } catch {}
    if (p.image_url) {
      try {
        const res = await fetch(`${BACKEND}${p.image_url}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `iihr-devotion-${p.id}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch {}
    }
    toast.success(p.image_url ? "Image downloaded + caption copied — paste & post!" : "Caption copied — paste & post!");
  };
  const toggleAuto = async () => {
    const next = !autoQueue;
    setAutoQueue(next);
    try { await api.post("/social/settings", { auto_queue_devotion: next }); toast.success(next ? "We'll auto-queue each morning's devotion" : "Auto-queue turned off"); }
    catch { setAutoQueue(!next); toast.error("Could not update setting"); }
  };

  return (
    <div className="max-w-2xl" data-testid="social-media">
      <div className="flex items-center gap-3 mb-1">
        <Share2 className="w-6 h-6 text-[#E6B34A]" />
        <h2 className="font-display font-extrabold text-2xl tracking-tight">Social Media</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">Compose a post (AI-assisted), then push it to your channels with one tap.</p>

      <label className="overline text-zinc-400">What are you posting?</label>
      <div className="mt-2 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {TYPES.map((t) => {
          const Icon = t.icon; const active = type === t.key;
          return (
            <button key={t.key} onClick={() => setType(t.key)} data-testid={`social-type-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {type === "event" && ctx?.events?.length > 0 && (
        <div className="mb-4">
          <label className="overline text-zinc-400">Pick an event</label>
          <select value={selEvent} onChange={(e) => setSelEvent(Number(e.target.value))} data-testid="social-event-pick" className="mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
            {ctx.events.map((e, i) => <option key={i} value={i}>{e.title}</option>)}
          </select>
        </div>
      )}

      <div className="mb-4">
        <label className="overline text-zinc-400">Details / notes for the AI</label>
        <Textarea value={context} onChange={(e) => setContext(e.target.value)} rows={4} data-testid="social-context" className={inputCls} placeholder="What should the post say?" />
      </div>

      <button onClick={() => generate("general")} disabled={busy} data-testid="social-generate" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 mb-5">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {caption ? "Regenerate caption" : "Generate caption"}
      </button>

      <div className="mb-4">
        <div className="flex items-center justify-between">
          <label className="overline text-zinc-400">Caption</label>
          <button onClick={() => copy(caption, "Caption")} disabled={!caption} data-testid="social-copy-caption" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-40"><Copy className="w-3.5 h-3.5" /> Copy</button>
        </div>
        <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={6} data-testid="social-caption" className={inputCls} placeholder="Your post text will appear here…" />
      </div>

      <div className="mb-6">
        <label className="overline text-zinc-400">Link to include</label>
        <div className="mt-1.5 flex items-center gap-2">
          <Input value={link} onChange={(e) => setLink(e.target.value)} data-testid="social-link" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
          <button onClick={() => copy(link, "Link")} disabled={!link} data-testid="social-copy-link" className="shrink-0 p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 disabled:opacity-40"><Link2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="mb-6" data-testid="social-image-block">
        <div className="flex items-center justify-between">
          <label className="overline text-zinc-400">Post image (square, for Instagram / TikTok)</label>
          <button onClick={genImage} disabled={imgBusy} data-testid="social-gen-image" className="text-xs font-semibold text-[#E6B34A] hover:text-[#CF9A34] inline-flex items-center gap-1.5 disabled:opacity-50">
            {imgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} {img ? "Regenerate" : "Generate image"}
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          {[["navy", "Navy", "#1A2452"], ["gold", "Gold", "#E6B34A"], ["sunrise", "Sunrise", "#C44E3E"]].map(([key, label, sw]) => (
            <button key={key} onClick={() => setTheme(key)} data-testid={`social-theme-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${theme === key ? "bg-white/15 text-white ring-1 ring-[#E6B34A]" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}>
              <span className="w-3 h-3 rounded-full" style={{ background: sw }} /> {label}
            </button>
          ))}
          <label className="ml-1 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 text-zinc-400 hover:bg-white/10 cursor-pointer transition-colors" data-testid="social-change-photo">
            <ImageIcon className="w-3.5 h-3.5" /> Change photo
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={changePhoto} className="hidden" data-testid="social-photo-input" />
          </label>
        </div>
        {img ? (
          <div className="mt-2 flex items-start gap-3">
            <img src={img} alt="Post" data-testid="social-image-preview" className="w-40 h-40 rounded-xl border border-white/10 object-cover" />
            <button onClick={downloadImage} data-testid="social-image-download" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 text-sm font-semibold transition-colors"><Download className="w-4 h-4" /> Download</button>
          </div>
        ) : (
          <p className="text-[11px] text-zinc-500 mt-1.5">Generate a branded square graphic to attach to your Instagram/TikTok post.</p>
        )}
      </div>

      <label className="overline text-zinc-400">Push to</label>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button onClick={shareFacebook} disabled={!caption && !link} data-testid="social-share-facebook" className="flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1877F2]/15 text-[#5B9BFF] hover:bg-[#1877F2]/25 font-semibold transition-colors disabled:opacity-40"><Facebook className="w-4 h-4" /> Facebook</button>
        <button onClick={shareX} disabled={!caption} data-testid="social-share-x" className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 font-semibold transition-colors disabled:opacity-40"><Twitter className="w-4 h-4" /> X</button>
        <button onClick={copyForInstagram} disabled={!caption} data-testid="social-share-instagram" className="flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E1306C]/15 text-[#F76FA0] hover:bg-[#E1306C]/25 font-semibold transition-colors disabled:opacity-40"><Instagram className="w-4 h-4" /> Instagram</button>
        <button onClick={copyForTikTok} disabled={!caption} data-testid="social-share-tiktok" className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 font-semibold transition-colors disabled:opacity-40"><Music2 className="w-4 h-4" /> TikTok</button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Facebook & X open a share window. Instagram & TikTok don't allow web posting — we copy your caption so you can paste it in the app (attach your image/video there).</p>

      <div className="mt-8 pt-6 border-t border-white/10" data-testid="social-scheduler">
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock className="w-5 h-5 text-[#E6B34A]" />
          <h3 className="font-display font-bold text-lg">Queue &amp; remind</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">Save this post and we'll remind you (in-app + push) to publish it at the best time each day.</p>

        <button onClick={toggleAuto} data-testid="social-auto-toggle" className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#141C42] border border-white/5 hover:border-[#E6B34A]/30 transition-colors mb-4 text-left">
          <div className={`shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${autoQueue ? "bg-[#E6B34A]" : "bg-white/15"}`}>
            <div className={`w-5 h-5 rounded-full bg-white transition-transform ${autoQueue ? "translate-x-5" : "translate-x-0"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#E6B34A]" /> Auto-queue the daily devotion</p>
            <p className="text-xs text-zinc-400 mt-0.5">Each morning's new devotion becomes a pre-filled post here, ready to publish.</p>
          </div>
          <span className={`shrink-0 text-xs font-bold ${autoQueue ? "text-[#E6B34A]" : "text-zinc-500"}`}>{autoQueue ? "On" : "Off"}</span>
        </button>

        <div className="p-4 rounded-2xl bg-[#141C42] border border-white/5">
          <label className="overline text-zinc-400">Best time to post</label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {bestTimes.map((b) => (
              <button key={b.time} onClick={() => setRemindAt(b.time)} data-testid={`social-time-${b.time}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${remindAt === b.time ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                {b.label}
              </button>
            ))}
            <div className="flex items-center gap-1.5 ml-1">
              <Clock className="w-4 h-4 text-zinc-400" />
              <input type="time" value={remindAt} onChange={(e) => setRemindAt(e.target.value)} data-testid="social-time-custom" className="bg-[#1E2A5A] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
            </div>
          </div>
          <button onClick={queuePost} disabled={queuing || !caption.trim()} data-testid="social-queue" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
            {queuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Queue this post
          </button>
          <p className="text-[11px] text-zinc-500 mt-2">Reminder time is US Eastern. You'll get a nudge every day until you mark it posted.</p>
        </div>

        {schedule.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="social-queue-list">
            <label className="overline text-zinc-400">Queued posts ({schedule.length})</label>
            {schedule.map((p) => (
              <div key={p.id} data-testid={`social-queued-${p.id}`} className="flex items-start gap-3 p-3.5 rounded-xl bg-[#101735] border border-white/5">
                <div className="shrink-0 flex flex-col items-center justify-center px-2.5 py-1.5 rounded-lg bg-[#E6B34A]/15 text-[#E6B34A]">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-bold mt-0.5 whitespace-nowrap">{fmtTime(p.remind_at)}</span>
                </div>
                {p.image_url && (
                  <a href={`${BACKEND}${p.image_url}`} target="_blank" rel="noopener noreferrer" data-testid={`social-queued-image-${p.id}`} className="shrink-0" title="Open branded image">
                    <img src={`${BACKEND}${p.image_url}`} alt="Post graphic" className="w-14 h-14 rounded-lg object-cover border border-white/10" />
                  </a>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                    {typeLabel(p.type)}
                    {p.auto && <span className="px-1.5 py-0.5 rounded bg-[#E6B34A]/20 text-[#E6B34A] text-[10px] font-bold tracking-normal">Auto</span>}
                    {p.image_url && <span className="text-zinc-500 normal-case tracking-normal">· image ready</span>}
                  </p>
                  <p className="text-sm text-zinc-200 line-clamp-2">{p.caption}</p>
                  {p.link && <p className="text-xs text-[#5B9BFF] truncate mt-0.5">{p.link}</p>}
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                  <button onClick={() => grabAndPost(p)} data-testid={`social-grab-${p.id}`} title="Download image + copy caption" className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors"><Download className="w-3.5 h-3.5" /> Grab &amp; post</button>
                  <button onClick={() => markPosted(p.id)} data-testid={`social-mark-posted-${p.id}`} title="Mark as posted" className="p-2 rounded-lg bg-white/5 hover:bg-emerald-500/20 text-zinc-400 hover:text-emerald-300 transition-colors"><CheckCircle2 className="w-4 h-4" /></button>
                  <button onClick={() => removeQueued(p.id)} data-testid={`social-remove-${p.id}`} title="Remove" className="p-2 rounded-lg bg-white/5 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
